import { array, option } from 'fp-ts';
import { Option } from 'fp-ts/Option';
import { constant, isEmpty } from 'lodash/fp';
import { createSelector } from 'reselect';

import {
    GUESTS_TERMS_AND_CONDITIONS_URL,
    LocalizedPropertyObject,
    MEWS_PRIVACY_POLICY_URL,
    MEWS_TERMS_AND_CONDITIONS_URL,
    Selector,
} from '@mews/framework';
import { Optional } from '@mews/std';
import { TextKey } from '@mews/text-keys';

import { getLanguageCode } from '../../../../modules/app/selectors';
import { getPlatformPrimaryUrl } from '../../../../modules/configuration/selectors';
import {
    getSelectedEnterprisePrivacyPolicyMarkdownValue,
    getSelectedEnterpriseTermsAndConditionsMarkdownValue,
} from '../../../../modules/entities/serviceDocuments/viewSelectors';
import {
    getEnterpriseServiceId,
    getServicePrivacyPolicyUrl,
    getServiceTermsAndConditionsUrl,
} from '../../../../modules/entities/services/selectors';
import { createLocalizePropertySelector } from '../../../../modules/globalization/selectors';
import { getSelectedHotelId } from '../../../../modules/resourceOrder/selectors';
import { getSelectedEnterpriseConfigurationServiceId } from '../../../../selectors/views/selectedEnterpriseConfiguration';
import { isSelectedEnterpriseModelMerchantOfRecord } from '../../../../selectors/views/selectedEnterprisePaymentConfiguration';
import { ApplicationState } from '../../../../types';
import { WithServiceId } from '../../../../types/selectors/selectorPropTypes';
import { createUrlSelector } from '../../../../utils/createUrlSelector';

const getServiceIdQuery = (serviceId: Optional<string>) => (serviceId != null ? `&serviceId=${serviceId}` : '');

const getSelectedHotelTermsPath = createSelector(
    getSelectedHotelId,
    (hotelId) => `${MEWS_TERMS_AND_CONDITIONS_URL}/${hotelId}`
);

const getPlatformPrimaryUrlWithLanguageCode = createUrlSelector(getPlatformPrimaryUrl, getLanguageCode);

const getGuestsTermsAndConditionsUrl = getPlatformPrimaryUrlWithLanguageCode(constant(GUESTS_TERMS_AND_CONDITIONS_URL));
const getDefaultHotelTermsAndConditions = getPlatformPrimaryUrlWithLanguageCode(getSelectedHotelTermsPath);
export const getMewsPrivacyPolicyUrl = getPlatformPrimaryUrlWithLanguageCode(constant(MEWS_PRIVACY_POLICY_URL));

interface Link {
    markdown: Option<string>;
    name: string;
    textKey: Mews.TextKey;
    url: Option<string>;
}

const getDefaultTermsAndConditionsWithServiceId = createSelector(
    getDefaultHotelTermsAndConditions,
    getSelectedEnterpriseConfigurationServiceId,
    (defaultHotelTermsAndConditions, serviceId) => `${defaultHotelTermsAndConditions}${getServiceIdQuery(serviceId)}`
);

const getGeneralGuestTermsAndConditionsLink = createSelector(
    isSelectedEnterpriseModelMerchantOfRecord,
    getGuestsTermsAndConditionsUrl,
    (isMerchantOfRecord, guestsTermsAndConditionsUrl): Option<Link> =>
        isMerchantOfRecord
            ? option.some({
                  name: 'guestGeneralTermsAndConditionsLink',
                  /*
                   * For historical reasons we host Guest general terms and conditions
                   * as Merchant terms and conditions.
                   * When we start hosting at correct url, naming can be made consistent.
                   */
                  url: option.some(guestsTermsAndConditionsUrl),
                  markdown: option.none,
                  textKey: TextKey.GuestsGeneralTermsAndConditions,
              })
            : option.none
);

const withSelectedEnterpriseServiceId =
    (selector: Selector<ApplicationState, WithServiceId, Optional<LocalizedPropertyObject>>) =>
    (state: ApplicationState) => {
        const enterpriseId = getSelectedHotelId(state);
        const serviceId = getEnterpriseServiceId(state, { enterpriseId });
        if (serviceId == null) {
            return null;
        }
        return selector(state, { serviceId });
    };

const getSelectedServiceTermsAndConditionsUrl = withSelectedEnterpriseServiceId(getServiceTermsAndConditionsUrl);
const getSelectedServicePrivacyPolicyUrl = withSelectedEnterpriseServiceId(getServicePrivacyPolicyUrl);

const getSelectedServiceLocalizedTermsAndConditionsUrl = createLocalizePropertySelector(
    getSelectedServiceTermsAndConditionsUrl
);

const getSelectedServiceLocalizedPrivacyPolicyUrl = createLocalizePropertySelector(getSelectedServicePrivacyPolicyUrl);

const getEnterpriseTermsAndConditionsLink = createSelector(
    getSelectedServiceLocalizedTermsAndConditionsUrl,
    getDefaultTermsAndConditionsWithServiceId,
    getSelectedEnterpriseTermsAndConditionsMarkdownValue,
    (hotelTermsAndConditionsUrl, defaultHotelTermsAndConditions, markdown): Option<Link> => {
        const url = option.fromNullable(
            isEmpty(hotelTermsAndConditionsUrl) ? defaultHotelTermsAndConditions : hotelTermsAndConditionsUrl
        );
        return option.some({
            name: 'enterpriseTermsAndConditionsLink',
            url,
            markdown,
            textKey: TextKey.PropertyTermsAndConditions,
        });
    }
);

const getEnterprisePrivacyPolicyLink = createSelector(
    getSelectedServiceLocalizedPrivacyPolicyUrl,
    getSelectedEnterprisePrivacyPolicyMarkdownValue,
    (propertyPrivacyPolicyUrl, markdown): Option<Link> =>
        option.some({
            name: 'enterprisePrivacyPolicyLink',
            url: option.fromNullable(propertyPrivacyPolicyUrl),
            markdown,
            textKey: TextKey.PropertyPrivacyPolicy,
        })
);

const getMewsPrivacyPolicyLink = createSelector(
    isSelectedEnterpriseModelMerchantOfRecord,
    getMewsPrivacyPolicyUrl,
    (isMerchantOfRecord, mewsPrivacyPolicyUrl): Option<Link> =>
        isMerchantOfRecord
            ? option.some({
                  name: 'mewsPrivacyPolicyLink',
                  url: option.some(mewsPrivacyPolicyUrl),
                  markdown: option.none,
                  textKey: TextKey.MewsPrivacyPolicy,
              })
            : option.none
);

export const getTermsAndConditionsLinks = createSelector(
    getGeneralGuestTermsAndConditionsLink,
    getEnterpriseTermsAndConditionsLink,
    getEnterprisePrivacyPolicyLink,
    getMewsPrivacyPolicyLink,
    (...args: Option<Link>[]): Link[] => array.compact(args)
);
